import { ParagraphText } from "../../../../Libraries";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { Box, capitalize } from "@mui/material";
import { ListTableAmountCell } from "../../../common";
import SidebarBodyContent from "../../../common/UBRightSidebar/SidebarBodyContent";
import { useAuthContext } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  calculateTotalOutstanding,
  findCustomerByID,
} from "../../../../database/helpers/customer";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import {
  bulkDeleteOrders,
  bulkRestoreStock,
} from "../../../../database/helpers/orders";
import { bulkDeleteInventory } from "../../../../database/helpers/inventory";
import {
  bulkCreatePayments,
  bulkDeletePaymentsV2,
  getPaymentsByInvoiceExternal,
} from "../../../../database/helpers/payment";
import { useContext, useState } from "react";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { Sync } from "../../../../SyncService";
import withObservables from "@nozbe/with-observables";
import { catchError, of } from "rxjs";
import { LanguageContext } from "../../../../context/LanguageContext";
import RecordPaymentModal from "../../../common/Modals/RecordPaymentModal";
import styles from "./styles.module.css";
import { uuid } from "../../../../constants";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";
import { dateFormatInNumberWithSpace } from "../../../../utils";

const ItemList = ({
  sale,
  key,
  index,
  customerDetail,
  orders,
  refetchList,
  onEdit,
}) => {
  const { user, business } = useAuthContext();
  const navigate = useNavigate();

  const { showSnackbar } = useSnackbar();
  const [I18n] = useContext(LanguageContext);

  const addedCustomer = customerDetail || undefined;

  const [isOpenRecordPaymentModal, setIsOpenRecordPaymentModal] =
    useState(false);
  const [isLoadingRecordPayment, setIsLoadingRecordPayment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openDetailSidebar, setOpenDetailSidebar] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState("");

  let capitalizeStatus = sale?.["status"]
    ? capitalize(sale["status"]?.toString())
    : "";

  let capitalizeMethod = sale?.["method"]
    ? capitalize(sale["method"]?.toString())
    : "";

  const handleDelete = async (e) => {
    e.stopPropagation();
    triggerLogEvent(analytics, "transaction_sidebar_option_delete");
    setShowDeleteModal(false);
    setIsActionLoading(true);

    if (orders.length) {
      let toDelete = [];
      await Promise.all(
        orders.map(async (item) => {
          let stock = await item.stock.fetch();

          toDelete.push({
            original: item,
            updated: stock,
          });
        }),
      );

      if (toDelete.length) {
        await bulkRestoreStock(toDelete, sale.type);
        await bulkDeleteOrders(toDelete);
        const origin =
          sale.tranType === "bill" ? "invoice" : sale.tranType === "pos" ? "pos" : "cash";
        await bulkDeleteInventory(sale, toDelete, sale.type, origin);
      }
    }
    if (customerDetail) {
      let payments = await sale.payment.fetch();
      await bulkDeletePaymentsV2(payments);

      let total = await calculateTotalOutstanding(customerDetail);
      await customerDetail.updateTotalOutstanding(total);
    }

    await sale.markAsDeleted();
    refetchList();
    setOpenDetailSidebar(false);
    setIsActionLoading(false);
    triggerLogEvent(analytics, "sale_deleted");
    Sync(user.phone_number, navigate);
    showSnackbar({
      message: I18n.transaction_deleted,
      severity: "success",
      icon: "",
    });
  };

  const handleOpenRecordPaymentModal = () => {
    setSelectedSale(sale);
    setIsOpenRecordPaymentModal(true);
  };

  const handleRecordPayment = (
    selectedMethod,
    selectedDate,
    receivedAmount,
  ) => {
    if (sale?.tranType?.toLowerCase() === "bill") {
      handleEditInvoice(
        Number(receivedAmount) + Number(sale.advance_amount),
        selectedMethod,
        selectedDate,
        customerDetail,
        selectedSale,
      );
    } else {
      handleEditQuickSale(
        Number(receivedAmount) + Number(sale.advance_amount),
        selectedMethod,
        selectedDate,
      );
    }
  };

  const handleEditQuickSale = async (
    amountRecvd,
    paymentMethod,
    transaction_date,
  ) => {
    const type = sale?.type;

    setIsLoadingRecordPayment(true);
    triggerLogEvent(analytics, "sale_record");
    const invoiceAmountObj = {
      discount: sale?.discount || 0,
      discount_unit: sale?.discount_unit || "rs",
      tax_rate: sale?.tax_rate || 0,
      total: 0,
      sub_total: sale?.sub_total || 0,
      advance_amount: sale?.advance_amount || 0,
      profit: sale?.profit || 0,
    };
    let invoiceAmount = Number(sale?.amount) || 0;
    let invoiceStatus =
      invoiceAmount - (Number(amountRecvd) || 0) === 0 ? "paid" : "unpaid";
    let partialAmount = invoiceAmount - (Number(amountRecvd) || 0);
    const currentMethod = !paymentMethod ? sale?.method : paymentMethod;

    const fetchPaidTo = selectedSale?.customer?.id
      ? await findCustomerByID(selectedSale?.customer?.id)
      : null;

    const dataToEdit = {
      customer: selectedSale?.customer?.id ? fetchPaidTo : null,
      recording_uri: sale?.recording_uri,
      advance_amount: Number(amountRecvd) || 0,
      invoice_number: sale?.invoice_number || business.invoice_number_count,
      invoice_prefix: sale?.invoice_prefix || business.invoice_number_prefix,
      invoice_title: sale?.invoice_title,
      discount_unit: sale?.discount_unit,
      sales_amount: sale?.sales_amount,
      document_url: sale?.document_url,
      cost_amount: sale?.cost_amount,
      attachment: sale?.attachment,
      sub_total: sale?.sub_total,
      signature: sale?.signature,
      tax_title: sale?.tax_title,
      tranType: sale?.tranType,
      image_url: sale?.image || null,
      date: transaction_date
        ? transaction_date
        : sale.transacted_at || new Date(),
      category: sale?.category,
      discount: sale?.discount,
      tax_rate: sale?.tax_rate,
      amount: invoiceAmount,
      method: currentMethod,
      wallet: currentMethod === "wallet" ? paymentMethod?.name : null,
      profit: sale?.profit,
      status: invoiceStatus,
      total: Number(invoiceAmountObj?.total) || 0,
      note: sale?.note || "",
      bank: currentMethod === "bank" ? paymentMethod?.name : null,
      partialAmount,
    };
    if (dataToEdit.customer) {
      let dataForPayment = {
        udhaar_note: dataToEdit?.note,
        date: dataToEdit?.date || new Date(),
        image_url: dataToEdit?.image_url || null,
        invoiceDetail: sale,
        type: "cashbook",
      };
      // if total amount is changed wrt to current invoice, we will create or update invoice full payment entry
      if (invoiceAmount != sale?.amount) {
        dataForPayment["amount"] = invoiceAmount;
        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "full_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          await dataToEdit.customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: type == "in" ? "Jama" : "Udhaar",
            external_type: "full_invoice",
          });
        }
      }
      if (Number(sale?.advance_amount) != Number(dataToEdit?.advance_amount)) {
        // if received or partial amount is changed wrt to current invoice, we will create or update invoice partial payment entry

        dataForPayment["amount"] = Number(dataToEdit?.advance_amount);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          let payObj = {
            date: dataToEdit?.date || new Date(),
            note: dataToEdit?.note || "",
            image_url: dataToEdit?.image_url || "",
            type: "cashbook",
            uid: uuid(),
            amount: Number(dataToEdit.advance_amount),
            payment_mode: type == "in" ? "Jama" : "Udhaar",
            external_type: "partial_invoice",
          };

          await bulkCreatePayments(business, dataToEdit.customer, sale, [
            payObj,
          ]);
        }
      }

      let totalOutstanding = await calculateTotalOutstanding(
        dataToEdit.customer,
      );

      await dataToEdit.customer.updateTotalOutstanding(totalOutstanding);
    }
    await sale.updateInvocie(dataToEdit);
    if (fetchPaidTo) triggerLogEvent(analytics, "sale_record_name");
    if (dataToEdit.advance_amount)
      triggerLogEvent(analytics, "sale_record_amount");
    if (dataToEdit.method) triggerLogEvent(analytics, "sale_record_method");
    setIsLoadingRecordPayment(false);
    setIsOpenRecordPaymentModal(false);
    setOpenDetailSidebar(false);
    refetchList();
    Sync(user?.phone_number, navigate);
    showSnackbar({
      message: I18n.update_record_sucessfully,
      severity: "success",
    });
  };

  const handleInvoicePaymentMethod = async (data, createdInvoice) => {
    let { method, bank, wallet, advance_amount } = data;

    let params = {
      method: method || "cash",
      bank,
      wallet,
      amount: advance_amount,
    };
    try {
      let invPayments = await createdInvoice?.invoice_payments.fetch();
      if (!invPayments?.length) {
        await createdInvoice.createTransactionPayments(params);
      } else {
        await invPayments[0].updateInvPayment(params);
      }
    } catch (error) {}
  };

  const updateInvoiceData = async (data) => {
    selectedSale.updateInvocie(data).then(async () => {
      await Sync(user?.phone_number, navigate);
    });
  };

  const handleEditInvoice = async (
    amountRecvd,
    paymentMethod,
    selected_date,
    customer,
    invoice,
  ) => {
    const type = invoice?.type;

    const invoiceCount = business.invoice_number_count;
    const invoiceAmountObj = {
      discount: invoice?.discount || 0,
      discount_unit: invoice?.discount_unit || "rs",
      tax_rate: invoice?.tax_rate || 0,
      total: 0,
      sub_total: invoice?.sub_total || 0,
      advance_amount: invoice?.advance_amount || 0,
      profit: invoice?.profit || 0,
    };
    let invoiceAmount = Number(invoice?.amount) || 0;
    let invoiceStatus =
      invoiceAmount - (Number(amountRecvd) || 0) === 0 ? "paid" : "unpaid";
    let partialAmount = invoiceAmount - (Number(amountRecvd) || 0);
    const inv_items = await invoice.orders.fetch();
    const currentMethod = !paymentMethod ? invoice?.method : paymentMethod;

    let invoiceData = {
      sales_amount: type == "in" ? invoiceAmount : 0,
      cost_amount: type == "out" ? invoiceAmount : 0,
      amount: invoiceAmount,
      date: selected_date ? selected_date : invoice.transacted_at || new Date(),
      customer: customer,
      items: inv_items || [],
      image_url: invoice?.image_url || "",
      document_url: invoice?.document_url || "",
      signature: invoice?.signature,
      attachment: invoice?.attachment || [],
      note: invoice?.note,
      status: invoiceStatus,
      method: currentMethod,
      type: invoice?.type,
      tranType: "bill",
      bank:
        currentMethod === "bank" ? paymentMethod?.name : invoice?.bank || null,
      wallet:
        currentMethod === "wallet"
          ? paymentMethod?.name
          : invoice?.bank || null,
      invoice_title: invoice?.invoice_title,
      invoice_number_count: invoiceCount + 1,
      invoice_number: invoice?.invoice_number || business.invoice_number_count,
      invoice_prefix: invoice?.invoice_prefix || business.invoice_number_prefix,
      discount: Number(invoiceAmountObj?.discount) || 0,
      discount_unit: invoiceAmountObj?.discount_unit,
      tax_rate: Number(invoiceAmountObj?.tax_rate) || 0,
      total: Number(invoiceAmountObj?.total) || 0,
      sub_total: Number(invoiceAmountObj?.sub_total) || 0,
      advance_amount: Number(amountRecvd) || 0,
      profit: Number(invoiceAmountObj?.profit) || 0,
      partialAmount,
    };
    if (customer && !addedCustomer) {
      await handleCreateCustomerPayment(customer, invoiceData, invoice);
    } else if (customer) {
      let dataForPayment = {
        udhaar_note: invoiceData?.note,
        date: invoiceData?.date || new Date(),
        image_url: invoiceData?.image_url || null,
        invoice,
        type: "invoicebook",
      };

      if (invoiceAmount != invoice?.amount) {
        dataForPayment["amount"] = invoiceAmount;
        let invoicePayment = await getPaymentsByInvoiceExternal(
          invoice?.id,
          "full_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0].updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: type == "in" ? "Udhaar" : "Jama",
            external_type: "full_invoice",
          });
        }
      } else if (Number(invoice?.advance_amount) != Number(amountRecvd)) {
        dataForPayment["amount"] = Number(amountRecvd);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          invoice?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0].updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: type == "in" ? "Jama" : "Udhaar",
            external_type: "partial_invoice",
          });
        }
      } else if (invoiceStatus == "paid") {
        dataForPayment["amount"] = Number(amountRecvd);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          invoice?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: type == "in" ? "Jama" : "Udhaar",
            external_type: "partial_invoice",
          });
        }
      }

      let totalOutstanding = await calculateTotalOutstanding(customer);

      await customer.updateTotalOutstanding(totalOutstanding);
    }

    await handleInvoicePaymentMethod(invoiceData, invoice);

    updateInvoiceData(invoiceData)
      .then((ans) => {
        refetchList();
        triggerLogEvent(
          analytics,
          type === "in" ? "inv_record_name" : "pb_record_name",
        );
        triggerLogEvent(
          analytics,
          type === "in" ? "inv_record_method" : "pb_record_method",
        );
        triggerLogEvent(
          analytics,
          type === "in" ? "inv_record_amount" : "pb_record_amount",
        );
        triggerLogEvent(analytics, type === "in" ? "inv_record" : "pb_record");
        setOpenDetailSidebar(false);
        setIsOpenRecordPaymentModal(false);
        showSnackbar({
          message: I18n.update_record_sucessfully,
          severity: "success",
        });
      })
      .catch((er) => {});
  };

  const handleCreateCustomerPayment = async (customer, data, invoice) => {
    const type = invoice?.type;

    let payObj = {
      date: data?.date || new Date(),
      udhaar_note: data?.note || "",
      imageURL: data?.image_url || "",
      type: "invoicebook",
    };

    let paymentArr = [
      {
        ...payObj,
        uid: uuid(),
        amount: data.amount,
        payment_mode: type == "in" ? "Udhaar" : "Jama",
        external_type: "full_invoice",
      },
    ];

    if (data?.advance_amount > 0) {
      paymentArr.unshift({
        ...payObj,
        uid: uuid(),
        amount: Number(data.advance_amount),
        payment_mode: type == "in" ? "Jama" : "Udhaar",
        external_type: "partial_invoice",
      });
    }

    await bulkCreatePayments(business, customer, invoice, paymentArr);

    let totalOutstanding = await calculateTotalOutstanding(customer);

    await customer.updateTotalOutstanding(totalOutstanding);
  };

  return (
    <Box
      id={`sale_list_${index}`}
      className="invoiceListTableBody AccountItemListWrapper"
      key={key}
      onClick={(e) => {
        if (showDeleteModal || openDetailSidebar || isOpenRecordPaymentModal)
          return;

        e.stopPropagation();
        setOpenDetailSidebar(true);
        triggerLogEvent(analytics, "sale_detail");
      }}
    >
      <Box
        className={`invListTableHeaderCol fiveColumn textLeft sidebarOption`}
      >
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor">
          {dateFormatInNumberWithSpace(sale?.transacted_at)}
        </ParagraphText>
      </Box>

      <Box
        className={`invListTableHeaderCol fiveColumn textLeft sidebarOption`}
      >
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor">
          {sale?.type == "in" ? I18n.sale : I18n.expense}
        </ParagraphText>
      </Box>

      <Box
        className={`invListTableHeaderCol fiveColumn textLeft sidebarOption`}
      >
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor">
          {sale?.type == "in"
            ? sale?.tranType == "bill"
              ? I18n.invoice_sale
              : sale?.tranType == "pos"
                ? I18n.pos_sale
                : I18n.quick_sale
            : sale?.tranType == "bill"
              ? I18n.purchase_order
              : I18n.expense}{" "}
          {sale?.invoice_prefix && sale?.invoice_number
            ? `• ${sale?.invoice_prefix?.replace("#", " #")}${sale?.invoice_number}`
            : ""}
        </ParagraphText>
      </Box>

      <Box className={`invListTableHeaderCol fiveColumn textLeft`}>
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor displayFlex">
          {(() => {
            // Status/method arrive in English; show the translated label when a key exists
            const translate = (text) => {
              const value = I18n[String(text || "").toLowerCase()];
              return value ? capitalize(value) : text;
            };
            const statusText = translate(
              sale["status"]
                ? capitalizeStatus
                : sale?.amount - (sale?.advance_amount || 0) > 0
                  ? "Unpaid"
                  : "Paid",
            );

            const methodText = capitalizeMethod ? `/ ${translate(capitalizeMethod)}` : "";

            return `${statusText} ${methodText}`.trim();
          })()}
        </ParagraphText>
      </Box>

      <Box
        className={`invListTableHeaderCol fiveColumn textRight amountColumnNewWrapper`}
      >
        <ListTableAmountCell
          amountWrapperClassName={
            capitalizeStatus == "Paid"
              ? "newFontColor"
              : sale?.type == "in"
                ? "foundationOrangeColor"
                : "foundationGreenColor"
          }
        >
          {capitalizeStatus == "Paid" ? (sale?.type == "in" ? "+" : "-") : ""}
          Rs. {formatNum(Math.abs(sale.amount) || 0)}
        </ListTableAmountCell>
      </Box>

      {openDetailSidebar ? (
        <SidebarBodyContent
          onClose={() => setOpenDetailSidebar(false)}
          open={openDetailSidebar}
          quickSale={sale}
          refetchList={refetchList}
          business={business}
          handleDelete={() => setShowDeleteModal(true)}
          handleMarkAsPaid={() => {
            triggerLogEvent(analytics, "bo_sidebar_mark_paid");
            handleOpenRecordPaymentModal();
          }}
          handleEdit={onEdit}
          isShowEditBtn={sale?.tranType == "pos" ? false : true}
          isShowDeleteBtn={sale?.tranType == "pos" ? false : true}
          invoiceFileName={sale?.type == "in" ? "Rupin Sale Invoice" : "Rupin Expense Bill"}
        />
      ) : null}

      {showDeleteModal && (
        <ConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title={I18n.this_action_cannot_be_undone}
          message={I18n.are_you_sure_you_want_to_delete_transaction}
          confirmText={I18n.delete}
          id="transactions_delete_modal"
        />
      )}

      {isOpenRecordPaymentModal && sale?.id == selectedSale?.id ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <RecordPaymentModal
            handleConfirm={handleRecordPayment}
            className={styles.modalContainer}
            handleClose={() => setIsOpenRecordPaymentModal(false)}
            isOpen={isOpenRecordPaymentModal}
            title={I18n.record_payment}
            selectedInvoice={sale}
            isLoading={isLoadingRecordPayment}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const enhance = withObservables(["sale", "business"], ({ sale, business }) => ({
  customerDetail: sale?.customer
    ? sale.customer.observe().pipe(catchError(() => of(null)))
    : of(null),

  orders: sale?.orders ? sale.orders.observe() : of([]),
}));

export default enhance(ItemList);
